import { useEffect, useState } from "react";
import type { Team } from "@tacklefour/api-types";
import { cn } from "@/lib/cn";

type TeamLike = Pick<Team, "id" | "name" | "shortName"> & { logoUrl?: string | null };

const AFL_LOGO_BASE = "https://squiggle.com.au/wp-content/themes/squiggle/assets/images";
const NRL_LOGO_BASE = "https://www.nrl.com/.theme";

const TEAM_ASSETS: Record<string, { nickname: string; logo: string; colour: string }> = {
  "afl-ade": { nickname: "Crows", logo: `${AFL_LOGO_BASE}/Adelaide.png`, colour: "#0E1B3D" },
  adelaide: { nickname: "Crows", logo: `${AFL_LOGO_BASE}/Adelaide.png`, colour: "#0E1B3D" },
  "afl-bri": { nickname: "Lions", logo: `${AFL_LOGO_BASE}/Brisbane.png`, colour: "#7B1F3A" },
  brisbane: { nickname: "Lions", logo: `${AFL_LOGO_BASE}/Brisbane.png`, colour: "#7B1F3A" },
  "afl-car": { nickname: "Blues", logo: `${AFL_LOGO_BASE}/Carlton.png`, colour: "#0B2343" },
  carlton: { nickname: "Blues", logo: `${AFL_LOGO_BASE}/Carlton.png`, colour: "#0B2343" },
  "afl-col": { nickname: "Magpies", logo: `${AFL_LOGO_BASE}/Collingwood.png`, colour: "#1A1A1A" },
  collingwood: { nickname: "Magpies", logo: `${AFL_LOGO_BASE}/Collingwood.png`, colour: "#1A1A1A" },
  "afl-ess": { nickname: "Bombers", logo: `${AFL_LOGO_BASE}/Essendon.png`, colour: "#D71920" },
  essendon: { nickname: "Bombers", logo: `${AFL_LOGO_BASE}/Essendon.png`, colour: "#D71920" },
  "afl-fre": { nickname: "Dockers", logo: `${AFL_LOGO_BASE}/Fremantle.png`, colour: "#3D1B68" },
  fremantle: { nickname: "Dockers", logo: `${AFL_LOGO_BASE}/Fremantle.png`, colour: "#3D1B68" },
  "afl-gee": { nickname: "Cats", logo: `${AFL_LOGO_BASE}/Geelong.png`, colour: "#0D2A52" },
  geelong: { nickname: "Cats", logo: `${AFL_LOGO_BASE}/Geelong.png`, colour: "#0D2A52" },
  "afl-gcs": { nickname: "Suns", logo: `${AFL_LOGO_BASE}/GoldCoast.png`, colour: "#D71920" },
  goldcoast: { nickname: "Suns", logo: `${AFL_LOGO_BASE}/GoldCoast.png`, colour: "#D71920" },
  "afl-gws": { nickname: "Giants", logo: `${AFL_LOGO_BASE}/Giants.png`, colour: "#F47920" },
  gws: { nickname: "Giants", logo: `${AFL_LOGO_BASE}/Giants.png`, colour: "#F47920" },
  "afl-haw": { nickname: "Hawks", logo: `${AFL_LOGO_BASE}/Hawthorn.png`, colour: "#4D2814" },
  hawthorn: { nickname: "Hawks", logo: `${AFL_LOGO_BASE}/Hawthorn.png`, colour: "#4D2814" },
  "afl-mel": { nickname: "Demons", logo: `${AFL_LOGO_BASE}/Melbourne.png`, colour: "#0D2A52" },
  melbourne: { nickname: "Demons", logo: `${AFL_LOGO_BASE}/Melbourne.png`, colour: "#0D2A52" },
  "afl-nor": { nickname: "Kangaroos", logo: `${AFL_LOGO_BASE}/NorthMelbourne.png`, colour: "#1E5AA8" },
  northmelbourne: { nickname: "Kangaroos", logo: `${AFL_LOGO_BASE}/NorthMelbourne.png`, colour: "#1E5AA8" },
  "afl-por": { nickname: "Power", logo: `${AFL_LOGO_BASE}/PortAdelaide.png`, colour: "#00A1B2" },
  portadelaide: { nickname: "Power", logo: `${AFL_LOGO_BASE}/PortAdelaide.png`, colour: "#00A1B2" },
  "afl-ric": { nickname: "Tigers", logo: `${AFL_LOGO_BASE}/Richmond.png`, colour: "#F5C400" },
  richmond: { nickname: "Tigers", logo: `${AFL_LOGO_BASE}/Richmond.png`, colour: "#F5C400" },
  "afl-stk": { nickname: "Saints", logo: `${AFL_LOGO_BASE}/StKilda.png`, colour: "#D71920" },
  stkilda: { nickname: "Saints", logo: `${AFL_LOGO_BASE}/StKilda.png`, colour: "#D71920" },
  "afl-syd": { nickname: "Swans", logo: `${AFL_LOGO_BASE}/Sydney.png`, colour: "#D71920" },
  sydney: { nickname: "Swans", logo: `${AFL_LOGO_BASE}/Sydney.png`, colour: "#D71920" },
  "afl-wce": { nickname: "Eagles", logo: `${AFL_LOGO_BASE}/WestCoast.png`, colour: "#003087" },
  westcoast: { nickname: "Eagles", logo: `${AFL_LOGO_BASE}/WestCoast.png`, colour: "#003087" },
  "afl-wbd": { nickname: "Bulldogs", logo: `${AFL_LOGO_BASE}/Bulldogs.png`, colour: "#1E5AA8" },
  westernbulldogs: { nickname: "Bulldogs", logo: `${AFL_LOGO_BASE}/Bulldogs.png`, colour: "#1E5AA8" },

  "nrl-bri": { nickname: "Broncos", logo: `${NRL_LOGO_BASE}/broncos/badge-light.svg`, colour: "#7B1F3A" },
  broncos: { nickname: "Broncos", logo: `${NRL_LOGO_BASE}/broncos/badge-light.svg`, colour: "#7B1F3A" },
  "nrl-can": { nickname: "Raiders", logo: `${NRL_LOGO_BASE}/raiders/badge-light.svg`, colour: "#84BD00" },
  raiders: { nickname: "Raiders", logo: `${NRL_LOGO_BASE}/raiders/badge-light.svg`, colour: "#84BD00" },
  "nrl-cbr": { nickname: "Bulldogs", logo: `${NRL_LOGO_BASE}/bulldogs/badge-light.svg`, colour: "#0057A8" },
  bulldogs: { nickname: "Bulldogs", logo: `${NRL_LOGO_BASE}/bulldogs/badge-light.svg`, colour: "#0057A8" },
  "nrl-cro": { nickname: "Sharks", logo: `${NRL_LOGO_BASE}/sharks/badge-light.svg`, colour: "#10A8B8" },
  sharks: { nickname: "Sharks", logo: `${NRL_LOGO_BASE}/sharks/badge-light.svg`, colour: "#10A8B8" },
  "nrl-dol": { nickname: "Dolphins", logo: `${NRL_LOGO_BASE}/dolphins/badge-light.svg`, colour: "#D71920" },
  dolphins: { nickname: "Dolphins", logo: `${NRL_LOGO_BASE}/dolphins/badge-light.svg`, colour: "#D71920" },
  "nrl-gct": { nickname: "Titans", logo: `${NRL_LOGO_BASE}/titans/badge.svg`, colour: "#00A6D6" },
  titans: { nickname: "Titans", logo: `${NRL_LOGO_BASE}/titans/badge.svg`, colour: "#00A6D6" },
  "nrl-man": { nickname: "Sea Eagles", logo: `${NRL_LOGO_BASE}/sea-eagles/badge-light.svg`, colour: "#5C1733" },
  seaeagles: { nickname: "Sea Eagles", logo: `${NRL_LOGO_BASE}/sea-eagles/badge-light.svg`, colour: "#5C1733" },
  "nrl-mel": { nickname: "Storm", logo: `${NRL_LOGO_BASE}/storm/badge-light.svg`, colour: "#6A2C91" },
  storm: { nickname: "Storm", logo: `${NRL_LOGO_BASE}/storm/badge-light.svg`, colour: "#6A2C91" },
  "nrl-new": { nickname: "Knights", logo: `${NRL_LOGO_BASE}/knights/badge-light.svg`, colour: "#005DAA" },
  knights: { nickname: "Knights", logo: `${NRL_LOGO_BASE}/knights/badge-light.svg`, colour: "#005DAA" },
  "nrl-nzw": { nickname: "Warriors", logo: `${NRL_LOGO_BASE}/warriors/badge-light.svg`, colour: "#111111" },
  warriors: { nickname: "Warriors", logo: `${NRL_LOGO_BASE}/warriors/badge-light.svg`, colour: "#111111" },
  "nrl-nql": { nickname: "Cowboys", logo: `${NRL_LOGO_BASE}/cowboys/badge-light.svg`, colour: "#0B2C5F" },
  cowboys: { nickname: "Cowboys", logo: `${NRL_LOGO_BASE}/cowboys/badge-light.svg`, colour: "#0B2C5F" },
  "nrl-par": { nickname: "Eels", logo: `${NRL_LOGO_BASE}/eels/badge.svg`, colour: "#F9C80E" },
  eels: { nickname: "Eels", logo: `${NRL_LOGO_BASE}/eels/badge.svg`, colour: "#F9C80E" },
  "nrl-pen": { nickname: "Panthers", logo: `${NRL_LOGO_BASE}/panthers/badge-light.svg`, colour: "#191919" },
  panthers: { nickname: "Panthers", logo: `${NRL_LOGO_BASE}/panthers/badge-light.svg`, colour: "#191919" },
  "nrl-sou": { nickname: "Rabbitohs", logo: `${NRL_LOGO_BASE}/rabbitohs/badge-light.svg`, colour: "#1A7F45" },
  rabbitohs: { nickname: "Rabbitohs", logo: `${NRL_LOGO_BASE}/rabbitohs/badge-light.svg`, colour: "#1A7F45" },
  "nrl-sgi": { nickname: "Dragons", logo: `${NRL_LOGO_BASE}/dragons/badge-light.svg`, colour: "#D71920" },
  dragons: { nickname: "Dragons", logo: `${NRL_LOGO_BASE}/dragons/badge-light.svg`, colour: "#D71920" },
  "nrl-syd": { nickname: "Roosters", logo: `${NRL_LOGO_BASE}/roosters/badge.svg`, colour: "#183D6D" },
  roosters: { nickname: "Roosters", logo: `${NRL_LOGO_BASE}/roosters/badge.svg`, colour: "#183D6D" },
  "nrl-wst": { nickname: "Wests Tigers", logo: `${NRL_LOGO_BASE}/wests-tigers/badge-light.svg`, colour: "#F58220" },
  tigers: { nickname: "Wests Tigers", logo: `${NRL_LOGO_BASE}/wests-tigers/badge-light.svg`, colour: "#F58220" },
};

export function getTeamDisplayName(team: TeamLike): string {
  return TEAM_ASSETS[team.id]?.nickname ?? team.shortName;
}

export function getTeamColour(team: TeamLike): string {
  return TEAM_ASSETS[team.id]?.colour ?? "#284b68";
}

export function TeamLogo({
  team,
  className,
}: {
  team: TeamLike;
  className?: string;
}) {
  const asset = TEAM_ASSETS[team.id];
  const source = team.logoUrl ?? asset?.logo;
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [source]);

  if (source && !failed) {
    return (
      <img
        src={source}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={cn("h-10 w-10 shrink-0 object-contain", className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-lg text-[9px] font-black tracking-tight text-white shadow-inner", className)}
      style={{ backgroundColor: asset?.colour ?? "#284b68" }}
    >
      {team.shortName.slice(0, 3)}
    </span>
  );
}
